import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Container,
  Paper,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
  Fade,
  Grow,
  Zoom,
  Slide,
  alpha,
} from "@mui/material";
import {
  Visibility,
  TrackChanges,
  PeopleAlt,
  Close as CloseIcon,
  RocketLaunch,
  EmojiEvents,
  BusinessCenter,
  TrendingUp,
  ArrowForward,
} from "@mui/icons-material";
import { styled, keyframes } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
import TimelineDot from "@mui/lab/TimelineDot";
import Header from "./Header";
import Footer from "./Footer";
import PageTitle from "../Components/PageTitle";

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;
const GradientTextTitle = styled("span")(({ theme }) => ({
  background: "linear-gradient(135deg, #2E9E53 0%, #25D366 50%, #0B4064 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  fontWeight: "inherit",
  backgroundSize: "200% 200%",
  animation: `${shimmer} 3s ease-in-out infinite`,
}));

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;
const RotatingElement = styled(Box)(({ duration = 20 }) => ({
  animation: `${rotate} ${duration}s linear infinite`,
}));

// Styled Components
const GradientText = styled("span")(({ theme }) => ({
  background: "linear-gradient(135deg, #2E9E53 0%, #1e7b3f 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  fontWeight: "inherit",
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  background: "rgba(46, 158, 83, 0.1)",
  color: "#2E9E53",
  fontWeight: 700,
  padding: "8px 4px",
  "& .MuiChip-icon": {
    color: "#2E9E53",
  },
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: "24px",
  background: "#fff",
  border: "1px solid rgba(46, 158, 83, 0.1)",
  transition: "all 0.3s ease",
  cursor: "pointer",
  height: "100%",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 20px 40px rgba(46, 158, 83, 0.15)",
    borderColor: "#2E9E53",
  },
}));

const TeamCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: "32px",
  background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
  border: "1px solid rgba(46, 158, 83, 0.1)",
  transition: "all 0.3s ease",
  textAlign: "center",
  height: "100%",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 30px 60px rgba(46, 158, 83, 0.2)",
  },
}));

// Data
const recognitions = [
  { icon: <EmojiEvents />, label: "DPIIT Recognised", color: "#FFD700" },
  { icon: <BusinessCenter />, label: "iStart Registered", color: "#2E9E53" },
  { icon: <RocketLaunch />, label: "Incubated at BTH", color: "#FF6B6B" },
  { icon: <TrendingUp />, label: "Govt. Funded", color: "#4A90E2" },
];

const coreValues = [
  {
    icon: <Visibility sx={{ fontSize: 40 }} />,
    title: "Our Vision",
    // text: "To become the most trusted B2B dealer ordering and credit management platform, helping manufacturers, distributors, and wholesalers operate smarter, get paid faster, and grow confidently in a digital-first world.",
    text:"To become the most trusted ERP platform for manufacturers, distributors, and wholesalers — helping businesses operate smarter, automate financial workflows, and scale confidently in a digital-first world.",
    color: "#2E9E53",
  },
  {
    icon: <TrackChanges sx={{ fontSize: 40 }} />,
    title: "Our Mission",
    // text: "To simplify dealer management by providing a powerful mobile app and web platform that enables 24/7 ordering, automated credit control, real-time visibility, and faster payment cycles — all in one seamless system.",
    text:"To simplify enterprise operations by providing a powerful mobile-first ERP system that enables automated procurement, intelligent credit control, real-time inventory visibility, and seamless financial management — all unified in one platform.",
    color: "#FFA726",
  },
  {
    icon: <PeopleAlt sx={{ fontSize: 40 }} />,
    title: "Our Culture",
    text: "We are builders, problem-solvers, and business enablers who believe in automation over chaos, transparency over confusion, and growth through smart technology.",
    color: "#2196F3",
  },
];

// const teamMembers = [
//   {
//     name: "Arpesh Agrawal",
//     role: "CEO & Founder",
//     avatar:
//       "https://ui-avatars.com/api/?name=Arpesh+Agrawal&background=2E9E53&color=fff&size=150",
//     social: { linkedin: "#", twitter: "#", email: "arpesh@bizbridge.com" },
//   },
//   {
//     name: "Sanjeev Kumar",
//     role: "Chief Technology Officer",
//     avatar:
//       "https://ui-avatars.com/api/?name=Sanjeev+Kumar&background=2196F3&color=fff&size=150",
//     social: { linkedin: "#", github: "#", email: "sanjeev@bizbridge.com" },
//   },
//   {
//     name: "Ayushi Bansal",
//     role: "Product Designer",
//     avatar:
//       "https://ui-avatars.com/api/?name=Ayushi+Bansal&background=FF6B6B&color=fff&size=150",
//     social: { linkedin: "#", instagram: "#", email: "ayushi@bizbridge.com" },
//   },
// ];

const teamMembers = [
  {
    name: "Arpesh Agrawal",
    role: "Founder & CEO",
    avatar: "https://placehold.co/150x150/2E9E53/FFFFFF?text=AA",
    social: { linkedin: "", twitter: "#" },
  },
  {
    name: "Shreya Gupta",
    role: "Co-Founder",
    avatar: "https://placehold.co/150x150/2E9E53/FFFFFF?text=SG",
    social: { linkedin: "", twitter: "#" },
  },
  {
    name: "Sanjeev Kumar",
    role:"Full Stack Developer",
    // role: "Chief Technology Officer",
    avatar: "https://placehold.co/150x150/2E9E53/FFFFFF?text=SK",
    social: { linkedin: "", twitter: "#" },
  },
  {
    name: "Himanshu Saini",
    role: "ERP Developer",
    avatar: "https://placehold.co/150x150/2E9E53/FFFFFF?text=HS",
    social: { linkedin: "", twitter: "#" },
  },
  {
    name: "Nitin Saini",
    role: "ERP Developer",
    avatar: "https://placehold.co/150x150/2E9E53/FFFFFF?text=NS",
    social: { linkedin: "", twitter: "#" },
  },
  {
    name: "Ashish Harvara",
    role: "React Native Developer",
    avatar: "https://placehold.co/150x150/2E9E53/FFFFFF?text=AH",
    social: { linkedin: "", twitter: "#" },
  },
  {
    name: "Ayushi Bansal",
    role: "Product Designer",
    avatar: "https://placehold.co/150x150/2E9E53/FFFFFF?text=AB",
    social: { linkedin: "", twitter: "#" },
  },
  {
    name: "Vidhi Lal",
    role: "MERN Stack Developer",
    avatar: "https://placehold.co/150x150/2E9E53/FFFFFF?text=VL",
    social: { linkedin: "", twitter: "#" },
  },
  {
    name: "Aditi Hada",
    role: "UI/UX Designer",
    avatar: "https://placehold.co/150x150/2E9E53/FFFFFF?text=AH",
    social: { linkedin: "", twitter: "#" },
  },
  {
    name: "Dhruv Sharma",
    role: "React Native Developer",
    avatar: "https://placehold.co/150x150/2E9E53/FFFFFF?text=DS",
    social: { linkedin: "", twitter: "#" },
  },

  {
    name: "Chirag",
    role: "Tester",
    avatar: "https://placehold.co/150x150/2E9E53/FFFFFF?text=C",
    social: { linkedin: "", twitter: "#" },
  },
];

const timelineEvents = [
  {
    year: "Late 2024",
    event: "Concept & Foundation",
    description:
      "The idea for BizBridge was born, and our core team was assembled to tackle a big challenge.",
    icon: <EmojiEvents />,
  },
  {
    year: "Early 2025",
    event: "Platform Development",
    description:
      "Months of dedicated work led to the creation of our robust, feature-rich beta platform.",
    icon: <BusinessCenter />,
  },
  {
    year: "Mid 2025",
    event: "Official Launch",
    description:
      "BizBridge officially launched, onboarding our first set of valued customers and partners.",
    icon: <RocketLaunch />,
  },
  {
    year: "Future",
    event: "Expanding Horizons",
    description:
      "We are continuously working on new features, including advanced analytics and mobile apps.",
    icon: <TrendingUp />,
  },
];

export default function AdvancedAboutPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const GREEN_ACCENT = "#2E9E53";

  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  //   const GREEN_ACCENT = '#2E9E53';

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleDialogOpen = () => setIsDialogOpen(true);
  const handleDialogClose = () => setIsDialogOpen(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    console.log("Form submitted:", formData);
    setIsDialogOpen(false);
    setFormData({ name: "", email: "", phone: "", message: "" });
  };

  return (
    <>
      <Header />
      <PageTitle
        title="About Us - BizBridge | Transforming B2B Dealer Networks"
        // description="Learn about BizBridge's mission to revolutionize B2B dealer management with smart technology, 24/7 ordering, and automated credit control."
        description="Bridging the gap between traditional enterprise systems and modern, intelligent ERP automation"
        keywords="BizBridge, B2B Platform, Dealer Management, Business Technology"
        url="/about"
      />

      <Box sx={{ bgcolor: "#fff", width: "100%", overflow: "hidden" }}>
        {/* --- HERO SECTION WITH PARALLAX EFFECT --- */}
        <Box
          sx={{
            position: "relative",
            minHeight: { xs: "70vh", md: "80vh" },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage:
                "url(https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=2000&q=80)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundAttachment: "fixed",
              filter: "brightness(0.3)",
              transform: "scale(1.1)",
              zIndex: 1,
            },
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "linear-gradient(135deg, rgba(46,158,83,0.2) 0%, rgba(0,0,0,0.8) 100%)",
              zIndex: 2,
            },
          }}
        >
          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 3 }}>
            <Zoom in timeout={1000}>
              <Box sx={{ textAlign: "center", color: "#fff" }}>
                <Grow in timeout={800}>
                  <StyledChip
                    // icon={<RocketLaunch />}
                    label="ESTABLISHED 2025"
                    sx={{ mb: 4, px: 3, py: 2, fontSize: "1rem" }}
                  />
                </Grow>

                <Typography
                  variant="h1"
                  sx={{
                    fontSize: {
                      xs: "3rem",
                      sm: "4rem",
                      md: "5rem",
                      lg: "6rem",
                    },
                    fontWeight: 800,
                    mb: 2,
                    textShadow: "0 10px 30px rgba(0,0,0,0.3)",
                    lineHeight: 1.1,
                  }}
                >
                  About <GradientText>BizBridge</GradientText>
                </Typography>

                <Fade in timeout={1500}>
                  <Typography
                    variant="h5"
                    sx={{
                      maxWidth: "800px",
                      mx: "auto",
                      mb: 4,
                      opacity: 0.9,
                      fontSize: { xs: "1.1rem", md: "1.3rem" },
                    }}
                  >
                    {/* Bridging the gap between traditional dealer networks and
                    modern digital automation */}
                    Bridging the gap between traditional enterprise systems and
                    modern, intelligent ERP automation
                  </Typography>
                </Fade>

                <Slide direction="up" in timeout={2000}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      justifyContent: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      endIcon={<ArrowForward />}
                      sx={{
                        bgcolor: GREEN_ACCENT,
                        px: 4,
                        py: 1.5,
                        borderRadius: "50px",
                        fontSize: "1.1rem",
                        "&:hover": {
                          bgcolor: "#1e7b3f",
                          transform: "scale(1.05)",
                        },
                        transition: "all 0.3s",
                      }}
                      onClick={handleDialogOpen}
                    >
                      Join Our Journey
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      sx={{
                        color: "#fff",
                        borderColor: "#fff",
                        px: 4,
                        py: 1.5,
                        borderRadius: "50px",
                        fontSize: "1.1rem",
                        "&:hover": {
                          borderColor: GREEN_ACCENT,
                          bgcolor: "rgba(255,255,255,0.1)",
                        },
                      }}
                      href="#story"
                    >
                      Our Story
                    </Button>
                  </Box>
                </Slide>
              </Box>
            </Zoom>
          </Container>
        </Box>

        {/* --- WHO WE ARE SECTION WITH MODERN DESIGN --- */}
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 15 } }} id="story">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Zoom in timeout={800}>
                <Box>
                  <Grow in timeout={800}>
                    <Chip
                      label="WHO WE ARE"
                      sx={{
                        bgcolor: alpha("#2E9E53", 0.1),
                        color: "#2E9E53",
                        fontWeight: 800,
                        mb: 3,
                        px: 3,
                        py: 2.5,
                        fontSize: "0.9rem",
                        border: `1px solid ${alpha("#2E9E53", 0.3)}`,
                        "& .MuiChip-icon": { color: "#2E9E53" },
                      }}
                    />
                  </Grow>

                  <Typography
                    variant={isMobile ? "h4" : "h3"}
                    fontWeight={800}
                    sx={{
                      color: "#101828",
                      textAlign: { xs: "left", md: "left" },
                      mb: 3,
                      fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                      lineHeight: 1.2,
                    }}
                  >
                    {/* Powering the Future of <br /> */}
                    Powering the Future of
                    <Box
                      component="span"
                      sx={{
                        position: "relative",
                        zIndex: 1,
                        display: "inline-block",
                      }}
                    >
                      {/* Dealer */} Enterprise
                      <Box
                        component="span"
                        sx={{
                          position: "absolute",
                          bottom: 2,
                          left: 0,
                          width: "100%",
                          height: "14px",
                          bgcolor: "#c6fed9",
                          zIndex: -1,
                        }}
                      />
                    </Box>{" "}
                    {/* Networks */} Operations
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#667085",
                      fontSize: "1.1rem",
                      lineHeight: 1.8,
                      mb: 4,
                    }}
                  >
                    {/* BizBridge is a modern B2B platform that helps manufacturers,
                    distributors, and wholesalers manage dealer ordering, credit
                    control, and payments — all in one seamless mobile app and
                    web system. */}
                    BizBridge is a modern ERP platform that helps manufacturers,
                    distributors, and wholesalers manage operations, credit
                    control, and financial workflows — all in one seamless
                    mobile and web ecosystem.
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: "#667085",
                      fontSize: "1.1rem",
                      lineHeight: 1.8,
                      mb: 4,
                    }}
                  >
                    {/* From 24/7 ordering to automated interest calculation and
                    real-time analytics, we bridge the gap between manual
                    processes and smart business automation. */}
                    From automated procurement to real-time inventory tracking
                    and intelligent financial analytics, we bridge the gap
                    between legacy systems and smart ERP automation.
                  </Typography>

                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    {recognitions.map((item, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Paper
                          sx={{
                            p: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            borderRadius: 3,
                            bgcolor: "#f8fafc",
                            border: "1px solid rgba(46,158,83,0.1)",
                            transition: "all 0.3s",
                            "&:hover": {
                              borderColor: item.color,
                              transform: "translateX(8px)",
                              boxShadow: "0 10px 20px rgba(0,0,0,0.05)",
                            },
                          }}
                        >
                          <Box sx={{ color: item.color }}>{item.icon}</Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.label}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Zoom>
            </Grid>

            <Grid item xs={12} md={6}>
              <Zoom in timeout={1000}>
                <Box sx={{ position: "relative" }}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: -20,
                      right: -20,
                      width: 200,
                      height: 200,
                      borderRadius: "50%",
                      background: `radial-gradient(circle, ${GREEN_ACCENT}20 0%, transparent 70%)`,
                      zIndex: 1,
                    }}
                  />
                  <Box
                    component="img"
                    src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1300&q=80"
                    alt="Team collaboration"
                    sx={{
                      width: "100%",
                      height: "auto",
                      borderRadius: "30px",
                      boxShadow: "0 30px 60px rgba(0,0,0,0.1)",
                      position: "relative",
                      zIndex: 2,
                      border: "4px solid #fff",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: -20,
                      left: -20,
                      width: 150,
                      height: 150,
                      borderRadius: "50%",
                      background: `radial-gradient(circle, ${GREEN_ACCENT}20 0%, transparent 70%)`,
                      zIndex: 1,
                    }}
                  />
                </Box>
              </Zoom>
            </Grid>
          </Grid>
        </Container>

        {/* --- CORE VALUES SECTION WITH CARDS --- */}
        <Box sx={{ bgcolor: "#f8fafc", py: { xs: 8, md: 15 } }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: "center", mb: 8 }}>
              <Grow in timeout={800}>
                <Chip
                  label="WHAT WE STAND FOR"
                  sx={{
                    bgcolor: alpha("#2E9E53", 0.1),
                    color: "#2E9E53",
                    fontWeight: 800,
                    mb: 3,
                    px: 3,
                    py: 2.5,
                    fontSize: "0.9rem",
                    border: `1px solid ${alpha("#2E9E53", 0.3)}`,
                    "& .MuiChip-icon": { color: "#2E9E53" },
                  }}
                />
              </Grow>
              <Zoom in timeout={1000}>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 900,
                    color: "#0B4064",
                    fontFamily: "'Archivo Black', sans-serif",
                    fontSize: {
                      xs: "1.8rem",
                      sm: "2.2rem",
                      md: "3rem",
                      lg: "3.5rem",
                    },
                    lineHeight: 1.2,
                    mb: { xs: 1.5, md: 2 },
                  }}
                >
                  Our Core <GradientTextTitle>Values</GradientTextTitle>
                </Typography>
              </Zoom>
              <Typography
                variant="h5"
                sx={{
                  color: "#2D3142",
                  maxWidth: "800px",
                  mx: "auto",
                  mb: 4,
                  lineHeight: 1.8,
                  fontSize: { xs: "1.1rem", md: "1.3rem" },
                }}
              >
                The principles that guide everything we do
              </Typography>
            </Box>

            <Grid container spacing={4} rowGap={4}>
              {coreValues.map((item, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Zoom in timeout={500 + index * 200}>
                    <FeatureCard>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: 3,
                          bgcolor: `${item.color}10`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 3,
                          color: item.color,
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 700, mb: 2, color: "#101828" }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: "#667085", lineHeight: 1.8 }}
                      >
                        {item.text}
                      </Typography>
                    </FeatureCard>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* --- OUR JOURNEY TIMELINE --- */}
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 12 } }}>
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Grow in timeout={800}>
              <Chip
                label="OUR JOURNEY"
                sx={{
                  bgcolor: alpha("#2E9E53", 0.1),
                  color: "#2E9E53",
                  fontWeight: 800,
                  mb: 3,
                  px: 3,
                  py: 2.5,
                  fontSize: "0.9rem",
                  border: `1px solid ${alpha("#2E9E53", 0.3)}`,
                  "& .MuiChip-icon": { color: "#2E9E53" },
                }}
              />
            </Grow>
            <Zoom in timeout={1000}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 900,
                  color: "#0B4064",
                  fontFamily: "'Archivo Black', sans-serif",
                  fontSize: {
                    xs: "1.8rem",
                    sm: "2.2rem",
                    md: "3rem",
                    lg: "3.5rem",
                  },
                  lineHeight: 1.2,
                  mb: { xs: 1.5, md: 2 },
                }}
              >
                The <GradientTextTitle> Roadmap</GradientTextTitle>
              </Typography>
            </Zoom>
            <Typography
              variant="h5"
              sx={{
                color: "#2D3142",
                maxWidth: "800px",
                mx: "auto",
                mb: 4,
                lineHeight: 1.8,
                fontSize: { xs: "1.1rem", md: "1.3rem" },
              }}
            >
              How we're building the future of B2B dealer management
            </Typography>
          </Box>

          <Timeline position={isMobileOrTablet ? "right" : "alternate"}>
            {timelineEvents.map((item, index) => (
              <TimelineItem
                key={index}
                sx={{
                  minHeight: "100px",
                  mb: { xs: 5, md: 0 },
                  ...(isMobileOrTablet && {
                    "&::before": { display: "none" },
                  }),
                  "&:hover .timeline-dot": {
                    boxShadow: `0 0 0 6px ${GREEN_ACCENT}33`,
                  },
                  "&:hover .timeline-card": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 24px -10px rgba(0,0,0,0.1)",
                  },
                }}
              >
                {!isMobileOrTablet && (
                  <TimelineOppositeContent sx={{ m: "auto 0", px: 4 }}>
                    <Typography
                      variant="h3"
                      component="span"
                      sx={{
                        fontWeight: 900,
                        color: "#EAECF0",
                        fontSize: { xs: "1.5rem", md: "2.2rem" },
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.year}
                    </Typography>
                  </TimelineOppositeContent>
                )}

                <TimelineSeparator>
                  {index > 0 && (
                    <TimelineConnector
                      sx={{ bgcolor: "#F2F4F7", width: "2px", flexGrow: 1 }}
                    />
                  )}
                  <TimelineDot
                    className="timeline-dot"
                    sx={{
                      bgcolor: GREEN_ACCENT,
                      width: 20,
                      height: 20,
                      borderWidth: 4,
                      borderColor: "#fff",
                      borderStyle: "solid",
                      boxShadow: "none",
                      my: 2,
                      transition: "all 0.3s ease",
                    }}
                  />
                  {index < timelineEvents.length - 1 && (
                    <TimelineConnector
                      sx={{ bgcolor: "#F2F4F7", width: "2px", flexGrow: 1 }}
                    />
                  )}
                </TimelineSeparator>

                <TimelineContent
                  sx={{ py: "12px", px: isMobileOrTablet ? 2 : 4 }}
                >
                  {isMobileOrTablet && (
                    <Typography
                      variant="h4"
                      component="div"
                      sx={{
                        fontWeight: 900,
                        color: "#EAECF0",
                        mb: 1,
                        fontSize: "2rem",
                      }}
                    >
                      {item.year}
                    </Typography>
                  )}

                  <Paper
                    className="timeline-card"
                    elevation={0}
                    sx={{
                      p: 4,
                      borderRadius: 3,
                      border: "1px solid #F2F4F7",
                      transition: "all 0.3s ease",
                      textAlign: "left",
                      boxShadow: "4px 4px 6px -1px rgba(0, 0, 0, 0.05)",
                    }}
                  >
                    <Typography
                      variant="h6"
                      component="span"
                      fontWeight="800"
                      gutterBottom
                      sx={{ color: "#101828", display: "block", mb: 1 }}
                    >
                      {item.event}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#667085", lineHeight: 1.6 }}
                    >
                      {item.description}
                    </Typography>
                  </Paper>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </Container>

        {/* --- TEAM SECTION WITH SOCIAL LINKS --- */}
        <Box sx={{ bgcolor: "#f8fafc", py: { xs: 8, md: 15 } }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: "center", mb: 8 }}>
              <Grow in timeout={800}>
                <Chip
                  label="OUR TEAM"
                  sx={{
                    bgcolor: alpha("#2E9E53", 0.1),
                    color: "#2E9E53",
                    fontWeight: 800,
                    mb: 3,
                    px: 3,
                    py: 2.5,
                    fontSize: "0.9rem",
                    border: `1px solid ${alpha("#2E9E53", 0.3)}`,
                    "& .MuiChip-icon": { color: "#2E9E53" },
                  }}
                />
              </Grow>
              <Zoom in timeout={1000}>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 900,
                    color: "#0B4064",
                    fontFamily: "'Archivo Black', sans-serif",
                    fontSize: {
                      xs: "1.8rem",
                      sm: "2.2rem",
                      md: "3rem",
                      lg: "3.5rem",
                    },
                    lineHeight: 1.2,
                    mb: { xs: 1.5, md: 2 },
                  }}
                >
                  Meet the <GradientTextTitle> Minds</GradientTextTitle>
                </Typography>
              </Zoom>
              <Typography
                variant="h5"
                sx={{
                  color: "#2D3142",
                  maxWidth: "800px",
                  mx: "auto",
                  mb: 4,
                  lineHeight: 1.8,
                  fontSize: { xs: "1.1rem", md: "1.3rem" },
                }}
              >
                The passionate people behind BizBridge
              </Typography>
            </Box>

            <Grid container spacing={4} rowGap={6}>
              {teamMembers.map((member, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Zoom in timeout={500 + index * 200}>
                    <TeamCard>
                      <Avatar
                        src={member.avatar}
                        sx={{
                          width: 120,
                          height: 120,
                          mx: "auto",
                          mb: 3,
                          border: `3px solid ${GREEN_ACCENT}`,
                          boxShadow: `0 10px 20px ${GREEN_ACCENT}30`,
                        }}
                      />
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, mb: 1, color: "#101828" }}
                      >
                        {member.name}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: GREEN_ACCENT,
                          fontWeight: 600,
                          mb: 2,
                        }}
                      >
                        {member.role}
                      </Typography>
                    </TeamCard>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* --- CTA SECTION WITH PARALLAX --- */}
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 15 } }}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Paper
              sx={{
                p: { xs: 4, md: 10 },
                borderRadius: 8,
                background:
                  "linear-gradient(135deg, #2E9E53 0%, #45B7A0 50%, #0B4064 100%)",
                textAlign: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Animated Background */}
              <RotatingElement duration={60}>
                <Box
                  sx={{
                    position: "absolute",
                    top: -200,
                    right: -200,
                    width: 400,
                    height: 400,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.1)",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: -200,
                    left: -200,
                    width: 400,
                    height: 400,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.1)",
                  }}
                />
              </RotatingElement>

              <Box sx={{ position: "relative", zIndex: 2 }}>
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                >
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: "50%",
                      bgcolor: "rgba(255,255,255,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 4,
                    }}
                  >
                    <RocketLaunch sx={{ fontSize: 50, color: "#fff" }} />
                  </Box>
                </motion.div>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "2rem", md: "3.5rem" },
                    fontWeight: 800,
                    color: "#fff",
                    mb: 2,
                  }}
                >
                  {/* Ready to Transform Your Business? */}
                  Ready to Build Something Powerful?
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    color: "rgba(255,255,255,0.9)",
                    mb: 4,
                    maxWidth: "700px",
                    mx: "auto",
                  }}
                >
                  {/* Join 5000+ dealers who have already upgraded to smart B2B
                  management */}
                  Whether you need a custom ERP, a full-stack web app, or a complete tech solution — BizBridge has the expertise to deliver it. Let's talk.
                </Typography>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  justifyContent="center"
                >
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleDialogOpen}
                    sx={{
                      bgcolor: "#fff",
                      color: GREEN_ACCENT,
                      px: 6,
                      py: 2,
                      borderRadius: "50px",
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      "&:hover": {
                        bgcolor: "#f0f0f0",
                        transform: "scale(1.05)",
                      },
                      transition: "all 0.3s",
                    }}
                  >
                    Get Started Free
                  </Button>
                  {/* <Button
                    variant="outlined"
                    size="large"
                    sx={{
                      color: "#fff",
                      borderColor: "#fff",
                      px: 6,
                      py: 2,
                      borderRadius: "50px",
                      fontSize: "1.1rem",
                      borderWidth: 2,
                      "&:hover": {
                        borderColor: "#fff",
                        borderWidth: 2,
                        bgcolor: "rgba(255,255,255,0.1)",
                      },
                    }}
                  >
                    Schedule Demo
                  </Button> */}
                </Stack>
              </Box>
            </Paper>
          </motion.div>
        </Container>

        {/* --- CONTACT DIALOG --- */}
        <Dialog
          open={isDialogOpen}
          onClose={handleDialogClose}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 4,
              background: "linear-gradient(135deg, #0A1929 0%, #1A3B5C 100%)",
              p: 2,
            },
          }}
        >
          <DialogTitle sx={{ pt: 3, pb: 2 }}>
            <Typography
              variant="h4"
              fontWeight={800}
              textAlign="center"
              sx={{ color: "#fff" }}
            >
              Get in <GradientText>Touch</GradientText>
            </Typography>
            <IconButton
              onClick={handleDialogClose}
              sx={{
                position: "absolute",
                right: 16,
                top: 16,
                color: "#fff",
                "&:hover": { color: GREEN_ACCENT },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Box component="form" onSubmit={handleFormSubmit}>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="name"
                    label="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    sx={{
                      "& label": { color: "#B2B9C5" },
                      "& label.Mui-focused": { color: GREEN_ACCENT },
                      "& .MuiOutlinedInput-root": {
                        color: "#fff",
                        borderRadius: "16px",
                        "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
                        "&:hover fieldset": { borderColor: GREEN_ACCENT },
                        "&.Mui-focused fieldset": { borderColor: GREEN_ACCENT },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="email"
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    sx={{
                      "& label": { color: "#B2B9C5" },
                      "& label.Mui-focused": { color: GREEN_ACCENT },
                      "& .MuiOutlinedInput-root": {
                        color: "#fff",
                        borderRadius: "16px",
                        "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
                        "&:hover fieldset": { borderColor: GREEN_ACCENT },
                        "&.Mui-focused fieldset": { borderColor: GREEN_ACCENT },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="phone"
                    label="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    sx={{
                      "& label": { color: "#B2B9C5" },
                      "& label.Mui-focused": { color: GREEN_ACCENT },
                      "& .MuiOutlinedInput-root": {
                        color: "#fff",
                        borderRadius: "16px",
                        "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
                        "&:hover fieldset": { borderColor: GREEN_ACCENT },
                        "&.Mui-focused fieldset": { borderColor: GREEN_ACCENT },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="message"
                    label="Your Message"
                    multiline
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    sx={{
                      "& label": { color: "#B2B9C5" },
                      "& label.Mui-focused": { color: GREEN_ACCENT },
                      "& .MuiOutlinedInput-root": {
                        color: "#fff",
                        borderRadius: "16px",
                        "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
                        "&:hover fieldset": { borderColor: GREEN_ACCENT },
                        "&.Mui-focused fieldset": { borderColor: GREEN_ACCENT },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions
              sx={{ p: 4, pt: 2, justifyContent: "center", gap: 2 }}
            >
              <Button
                onClick={handleDialogClose}
                variant="outlined"
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: "50px",
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.3)",
                  "&:hover": {
                    borderColor: GREEN_ACCENT,
                    bgcolor: "rgba(46,158,83,0.1)",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: "50px",
                  bgcolor: GREEN_ACCENT,
                  "&:hover": { bgcolor: "#1e7b3f" },
                }}
              >
                Send Message
              </Button>
            </DialogActions>
          </Box>
        </Dialog>
        {/* <Container maxWidth="lg" sx={{ py: { xs: 8, md: 15 } }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 8 },
              borderRadius: 8,
              background: "linear-gradient(135deg, #0A1929 0%, #1A3B5C 100%)",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -100,
                right: -100,
                width: 300,
                height: 300,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${GREEN_ACCENT}40 0%, transparent 70%)`,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: -100,
                left: -100,
                width: 300,
                height: 300,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${GREEN_ACCENT}40 0%, transparent 70%)`,
              }}
            />

            <Box sx={{ position: "relative", zIndex: 2 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: `${GREEN_ACCENT}20`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3,
                }}
              >
                <RocketLaunch sx={{ fontSize: 40, color: GREEN_ACCENT }} />
              </Box>

              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "2rem", md: "3rem" },
                  fontWeight: 800,
                  color: "#fff",
                  mb: 2,
                }}
              >
                Ready to Transform Your <GradientText>Business?</GradientText>
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: "#B2B9C5",
                  mb: 4,
                  maxWidth: "700px",
                  mx: "auto",
                }}
              >
                Join 5000+ dealers who have already upgraded to smart B2B
                management
              </Typography>

              <Button
                variant="contained"
                size="large"
                onClick={handleDialogOpen}
                sx={{
                  bgcolor: GREEN_ACCENT,
                  px: 6,
                  py: 2,
                  borderRadius: "50px",
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  "&:hover": {
                    bgcolor: "#1e7b3f",
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.3s",
                  boxShadow: `0 10px 20px ${GREEN_ACCENT}40`,
                }}
              >
                Get Started Today
              </Button>
            </Box>
          </Paper>
        </Container> */}

        {/* <Dialog
          open={isDialogOpen}
          onClose={handleDialogClose}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 4,
              p: 2,
            },
          }}
        >
          <DialogTitle sx={{ pt: 3, pb: 2 }}>
            <Typography variant="h4" fontWeight={800} textAlign="center">
              Get in <GradientText>Touch</GradientText>
            </Typography>
            <IconButton
              onClick={handleDialogClose}
              sx={{
                position: "absolute",
                right: 16,
                top: 16,
                "&:hover": { color: GREEN_ACCENT },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Box component="form" onSubmit={handleFormSubmit}>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="name"
                    label="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="email"
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="phone"
                    label="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="message"
                    label="Your Message"
                    multiline
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    sx={textFieldStyles}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions
              sx={{ p: 4, pt: 2, justifyContent: "center", gap: 2 }}
            >
              <Button
                onClick={handleDialogClose}
                variant="outlined"
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: "50px",
                  color: GREEN_ACCENT,
                  borderColor: GREEN_ACCENT,
                  borderWidth: 2,
                  "&:hover": {
                    borderColor: "#1e7b3f",
                    borderWidth: 2,
                    bgcolor: `${GREEN_ACCENT}10`,
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: "50px",
                  bgcolor: GREEN_ACCENT,
                  "&:hover": { bgcolor: "#1e7b3f" },
                }}
              >
                Send Message
              </Button>
            </DialogActions>
          </Box>
        </Dialog> */}
      </Box>
      <Footer />
    </>
  );
}
